// @ts-nocheck
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { formatCurrency, formatDate } from '../lib/utils';

const VALID_TRANSITIONS: Record<string, string[]> = {
  missing:       ['received', 'requested', 'waived'],
  requested:     ['received', 'missing', 'waived'],
  received:      ['missing', 'requested', 'verified'],
  verified:      ['received', 'waived'],
  has_reference: ['received', 'verified', 'waived', 'missing'],
  waived:        ['missing'],
};

const STATUS_LABELS: Record<string, string> = {
  missing:   'Reset to Missing',
  requested: 'Revert to Requested',
  received:  'Mark Received',
  verified:  'Mark Verified',
  waived:    'Waive',
};

interface Transaction {
  id: number;
  transaction_date: string;
  transaction_type: string;
  reference_number: string | null;
  amount: number;
  cert_status: string;
  cert_number: string | null;
  client_name?: string;
}

interface TransactionTableProps {
  transactions: Transaction[];
  showClient?: boolean;
  selectable?: boolean;
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  onStatusChange?: (id: number, status: string, extra?: { cert_number?: string }) => void;
}

export function TransactionTable({
  transactions,
  showClient = false,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  onStatusChange,
}: TransactionTableProps) {
  const [sortField, setSortField] = useState<string>('transaction_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Track which row is in "enter cert number" mode
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [certInput, setCertInput] = useState('');

  const sorted = [...transactions].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[sortField];
    const bVal = (b as Record<string, unknown>)[sortField];
    const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleAll = () => {
    if (selectedIds.size === transactions.length) {
      onSelectionChange?.(new Set());
    } else {
      onSelectionChange?.(new Set(transactions.map(t => t.id)));
    }
  };

  const toggleOne = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.(next);
  };

  const handleDropdownChange = (txId: number, newStatus: string) => {
    if (!newStatus) return;
    if (newStatus === 'received') {
      // Show optional cert number input — staff can enter now or skip
      setEditingRow(txId);
      setCertInput('');
    } else {
      onStatusChange?.(txId, newStatus);
    }
  };

  const confirmCert = (txId: number) => {
    const extra = certInput.trim() ? { cert_number: certInput.trim() } : undefined;
    onStatusChange?.(txId, 'received', extra);
    setEditingRow(null);
    setCertInput('');
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setCertInput('');
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && (sortDir === 'asc' ? '\u2191' : '\u2193')}
      </span>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.size === transactions.length && transactions.length > 0}
                  onChange={toggleAll}
                  className="rounded border-gray-300"
                />
              </th>
            )}
            <SortHeader field="transaction_date">Date</SortHeader>
            {showClient && <SortHeader field="client_name">Client</SortHeader>}
            <SortHeader field="transaction_type">Type</SortHeader>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
            <SortHeader field="amount">Amount</SortHeader>
            <SortHeader field="cert_status">Status</SortHeader>
            {onStatusChange && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sorted.map((t) => (
            <tr key={t.id} className={`hover:bg-gray-50 ${editingRow === t.id ? 'bg-blue-50' : ''}`}>
              {selectable && (
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    className="rounded border-gray-300"
                  />
                </td>
              )}
              <td className="px-4 py-3 whitespace-nowrap">{formatDate(t.transaction_date)}</td>
              {showClient && <td className="px-4 py-3">{t.client_name}</td>}
              <td className="px-4 py-3">{t.transaction_type}</td>
              <td className="px-4 py-3 font-mono text-xs">{t.cert_number || t.reference_number || '-'}</td>
              <td className="px-4 py-3 text-right font-mono">{formatCurrency(t.amount)}</td>
              <td className="px-4 py-3"><StatusBadge status={t.cert_status} /></td>
              {onStatusChange && (
                <td className="px-4 py-3">
                  {editingRow === t.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={certInput}
                        onChange={(e) => setCertInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmCert(t.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        placeholder="Cert # (optional)"
                        className="w-32 text-xs border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={() => confirmCert(t.id)}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                        title="Confirm (cert # optional)"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <select
                      value=""
                      onChange={(e) => handleDropdownChange(t.id, e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                    >
                      <option value="">Change...</option>
                      {VALID_TRANSITIONS[t.cert_status]?.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                  )}
                </td>
              )}
            </tr>
          ))}
          {transactions.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                No transactions found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
